import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ErrorState from '@components/ui/ErrorState';
import SigningRequests, { SigningRequestSummary } from '@components/ui/SigningRequests';
import './PeopleDetail.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LinkedProject {
  id: string;
  project_name: string;
  status: string;
  company_id: string | null;
  company_name: string | null;
  lead_person_id: string | null;
  created_at: string;
}

interface PersonDocument {
  id: string;
  document_name: string;
  document_type: string;
  uploaded_at: string;
  created_at: string;
}

interface PersonDetail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_id: string | null;
  company_name: string | null;
  role_or_title: string | null;
  status: string;
  source: string | null;
  newsletter_consent: boolean;
  website_or_profile_links: string | null;
  general_notes: string | null;
  merged_into_person_id?: string | null;
  client_username?: string | null;
  client_access_enabled?: number | boolean;
  created_at: string;
  updated_at: string;
  linked_projects: LinkedProject[];
  documents?: PersonDocument[];
  signing_requests?: SigningRequestSummary[];
}

interface MergeCandidate {
  id: string;
  full_name: string;
  email: string | null;
  company_name: string | null;
  status: string;
}

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

const PERSON_STATUSES = [
  'Subscriber',
  'Prospect',
  'Attempted to contact',
  'Connected',
  'Qualified',
  'Opportunity',
  'Customer',
  'Unqualified',
] as const;

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Customer':
      return 'success';
    case 'Opportunity':
    case 'Qualified':
      return 'warning';
    case 'Unqualified':
      return 'danger';
    default:
      return 'default';
  }
}

function projectStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Work Started':
    case 'Proposal Accepted':
      return 'warning';
    default:
      return 'default';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Edit form state ──────────────────────────────────────────────────────────

interface EditFormState {
  full_name: string;
  email: string;
  phone: string;
  role_or_title: string;
  status: string;
  newsletter_consent: boolean;
  website_or_profile_links: string;
  general_notes: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PeopleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [person, setPerson] = useState<PersonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<EditFormState>({
    full_name: '',
    email: '',
    phone: '',
    role_or_title: '',
    status: '',
    newsletter_consent: false,
    website_or_profile_links: '',
    general_notes: '',
  });

  // Header quick actions state (status, merge, delete)
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Create Project quick-create state
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [createProjectName, setCreateProjectName] = useState('');
  const [createProjectDocId, setCreateProjectDocId] = useState<string>('');
  const [createProjectCreateRepo, setCreateProjectCreateRepo] = useState(false);
  const [createProjectSaving, setCreateProjectSaving] = useState(false);
  const [createProjectError, setCreateProjectError] = useState<string | null>(null);

  // Client portal access state
  const [grantFormOpen, setGrantFormOpen] = useState(false);
  const [grantUsername, setGrantUsername] = useState('');
  const [clientAccessSaving, setClientAccessSaving] = useState(false);
  const [clientAccessError, setClientAccessError] = useState<string | null>(null);
  const [clientAccessResult, setClientAccessResult] = useState<
    { email_sent: boolean; set_password_url?: string } | null
  >(null);

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocError, setUploadDocError] = useState<string | null>(null);
  const [uploadDocFile, setUploadDocFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<string>('Meeting notes');
  const [uploadDocOpen, setUploadDocOpen] = useState(false);

  // Merge UI state (Xero-style: merge another person INTO this person)
  // Spec: selection must be search-by-name/typeahead (not manual ID entry)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState<MergeCandidate[]>([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeSelected, setMergeSelected] = useState<MergeCandidate | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeSaving, setMergeSaving] = useState(false);

  const fetchPerson = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/people/${id}`);
      if (res.status === 404) {
        setError('Person not found.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { success: boolean; data: PersonDetail };
      if (!json.success) throw new Error('API returned failure');
      setPerson(json.data);
    } catch (err) {
      console.error('Failed to fetch person:', err);
      setError('Failed to load person. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const resetMergeState = () => {
    setMergeOpen(false);
    setMergeQuery('');
    setMergeResults([]);
    setMergeSearching(false);
    setMergeSelected(null);
    setMergeError(null);
    setMergeSaving(false);
  };

  const resetCreateProjectState = () => {
    setCreateProjectOpen(false);
    setCreateProjectName('');
    setCreateProjectDocId('');
    setCreateProjectCreateRepo(false);
    setCreateProjectSaving(false);
    setCreateProjectError(null);
  };

  const resetUploadDocState = () => {
    setUploadDocFile(null);
    setUploadDocType('Meeting notes');
    setUploadDocError(null);
    setUploadDocOpen(false);
  };

  const handleUploadDocument = async () => {
    if (!id || !person) return;
    if (!uploadDocFile) {
      setUploadDocError('Please select a file to upload.');
      return;
    }

    setUploadingDoc(true);
    setUploadDocError(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadDocFile);
      formData.append('document_name', uploadDocFile.name);
      formData.append('document_type', uploadDocType);
      formData.append('person_id', id);

      const res = await fetch('/api/documents', {
        method: 'POST',
        body: formData,
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || (json as any)?.success !== true) {
        throw new Error((json as any)?.message ?? `Upload failed (HTTP ${res.status})`);
      }

      // Refresh person detail to show new document
      await fetchPerson();
      resetUploadDocState();
    } catch (err: unknown) {
      setUploadDocError(err instanceof Error ? err.message : 'Failed to upload document.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const openUploadDocDialog = () => {
    setUploadDocError(null);
    setUploadDocFile(null);
    setUploadDocType('Meeting notes');
    setUploadDocOpen(true);
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!id || !person) return;

    const ok = window.confirm(
      `Are you sure you want to delete this document?\n\n${docName}\n\nThis action cannot be undone.`
    );
    if (!ok) return;

    try {
      const res = await fetch(`/api/documents/${docId}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || (json as any)?.success !== true) {
        throw new Error((json as any)?.message ?? `Delete failed (HTTP ${res.status})`);
      }

      // Refresh person detail to remove deleted document
      await fetchPerson();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete document.');
    }
  };

  const openGrantForm = () => {
    setGrantUsername(person?.email ?? '');
    setClientAccessError(null);
    setClientAccessResult(null);
    setGrantFormOpen(true);
  };

  const submitGrantClientAccess = async () => {
    if (!id || !person) return;
    const isInitialGrant = !person.client_username;
    if (isInitialGrant && !grantUsername.trim()) {
      setClientAccessError('Username is required.');
      return;
    }
    if (!person.email) {
      setClientAccessError('This person needs an email address on file before granting client access.');
      return;
    }

    setClientAccessSaving(true);
    setClientAccessError(null);
    try {
      const res = await fetch('/api/client-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: id,
          ...(isInitialGrant ? { username: grantUsername.trim() } : {}),
        }),
      });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }
      setClientAccessResult({
        email_sent: (json as any).data.email_sent,
        set_password_url: (json as any).data.set_password_url,
      });
      setGrantFormOpen(false);
      await fetchPerson();
    } catch (err: unknown) {
      setClientAccessError(err instanceof Error ? err.message : 'Failed to grant client access.');
    } finally {
      setClientAccessSaving(false);
    }
  };

  const handleRevokeClientAccess = async () => {
    if (!id || !person) return;
    const ok = window.confirm(
      `Revoke client portal access for ${person.full_name}?\n\nThis signs them out immediately. Their username is kept, so re-granting later won't require a new password.`
    );
    if (!ok) return;

    setClientAccessSaving(true);
    setClientAccessError(null);
    try {
      const res = await fetch(`/api/client-users?person_id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }
      setClientAccessResult(null);
      await fetchPerson();
    } catch (err: unknown) {
      setClientAccessError(err instanceof Error ? err.message : 'Failed to revoke client access.');
    } finally {
      setClientAccessSaving(false);
    }
  };

  const [previewingClient, setPreviewingClient] = useState(false);

  const handlePreviewClientSession = async () => {
    if (!id) return;
    setPreviewingClient(true);
    setClientAccessError(null);
    try {
      const res = await fetch(`/api/people/${id}/client-session-preview`, { method: 'POST' });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }
      window.open('/client', '_blank', 'noopener');
    } catch (err: unknown) {
      setClientAccessError(err instanceof Error ? err.message : 'Failed to open client preview.');
    } finally {
      setPreviewingClient(false);
    }
  };

  const openMergePanel = () => {
    setMergeOpen(true);
    setMergeQuery('');
    setMergeResults([]);
    setMergeSearching(false);
    setMergeSelected(null);
    setMergeError(null);
    setMergeSaving(false);
  };

  const searchMergeCandidates = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) {
        setMergeResults([]);
        return;
      }
      setMergeSearching(true);
      try {
        const res = await fetch(`/api/people?q=${encodeURIComponent(q)}&page_size=10`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ApiListResponse<MergeCandidate>;
        if (!json.success) throw new Error('Search failed');

        // Exclude the current person (cannot merge into itself)
        const filtered = (json.data ?? []).filter((p) => p.id !== id);
        setMergeResults(filtered);
      } catch (err) {
        console.error('Merge search failed:', err);
        setMergeResults([]);
      } finally {
        setMergeSearching(false);
      }
    },
    [id]
  );

  useEffect(() => {
    if (!mergeOpen) return;

    // If a person is already selected, don't keep searching unless query diverges.
    if (mergeSelected && mergeQuery.trim() === mergeSelected.full_name.trim()) return;

    const q = mergeQuery.trim();
    if (q.length < 2) {
      setMergeResults([]);
      return;
    }

    const timer = setTimeout(() => {
      searchMergeCandidates(mergeQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [mergeOpen, mergeQuery, mergeSelected, searchMergeCandidates]);

  const confirmMerge = async () => {
    if (!id || !person) return;
    if (!mergeSelected) {
      setMergeError('Select a duplicate person to merge.');
      return;
    }

    const duplicateId = mergeSelected.id;
    if (duplicateId === id) {
      setMergeError('You cannot merge a person into itself.');
      return;
    }

    const ok = window.confirm(
      `Merge duplicate into primary?\n\nPrimary: ${person.full_name} (${person.id})\nDuplicate: ${mergeSelected.full_name} (${duplicateId})\n\nPrimary record remains. Duplicate is marked as merged and hidden from lists.`
    );
    if (!ok) return;

    setMergeSaving(true);
    setMergeError(null);
    try {
      const res = await fetch('/api/people/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_person_id: id,
          duplicate_person_id: duplicateId,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }

      await fetchPerson();
      resetMergeState();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to merge people.';
      setMergeError(message);
    } finally {
      setMergeSaving(false);
    }
  };

  const [statusDraft, setStatusDraft] = useState('');

  useEffect(() => {
    if (person?.status) setStatusDraft(person.status);
  }, [person?.status]);

  const handleSaveStatus = async (nextStatus: string) => {
    if (!id || !person) return;
    if (!nextStatus || nextStatus === person.status) return;

    setStatusSaving(true);
    setStatusError(null);
    try {
      const res = await fetch(`/api/people/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as any)?.success) {
        throw new Error((json as any)?.message ?? `HTTP ${res.status}`);
      }

      setPerson((prev) => {
        if (!prev) return (json as any).data;
        return {
          ...(json as any).data,
          linked_projects: prev.linked_projects,
          documents: prev.documents,
        };
      });
    } catch (err: unknown) {
      setStatusError(err instanceof Error ? err.message : 'Failed to update status.');
      // Revert dropdown back to stored value.
      setStatusDraft(person.status);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDeletePerson = async () => {
    if (!id || !person) return;

    const ok = window.confirm(
      `Are you sure you want to delete this person?\n\n${person.full_name}\n\nThis action cannot be undone.`
    );
    if (!ok) return;

    setDeleteSaving(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/people/${id}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as any)?.success) {
        const details = (json as any)?.details ? `\n\n${JSON.stringify((json as any).details)}` : '';
        throw new Error(((json as any)?.message ?? `HTTP ${res.status}`) + details);
      }
      navigate('/people');
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete person.');
    } finally {
      setDeleteSaving(false);
    }
  };

  useEffect(() => {
    fetchPerson();
  }, [fetchPerson]);

  const openCreateProjectDialog = () => {
    if (!person) return;
    setCreateProjectName('');
    setCreateProjectDocId('');
    setCreateProjectCreateRepo(false);
    setCreateProjectError(null);
    setCreateProjectOpen(true);
  };

  const handleCreateProject = async () => {
    if (!id || !person) return;
    if (!person.company_id) {
      setCreateProjectError('This person is not linked to a company, so a project cannot be created yet.');
      return;
    }
    if (!createProjectName.trim()) {
      setCreateProjectError('Project name is required.');
      return;
    }

    setCreateProjectSaving(true);
    setCreateProjectError(null);
    try {
      // 1) Create the CRM project first.
      const createRes = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: createProjectName.trim(),
          company_id: person.company_id,
          lead_person_id: person.id,
          linked_person_ids: [],
          requirements_document_id: createProjectDocId ? createProjectDocId : undefined,
          status: 'New',
        }),
      });

      const createJson = await createRes.json().catch(() => ({} as any));
      if (!createRes.ok || (createJson as any)?.success !== true) {
        throw new Error((createJson as any)?.message ?? `Project create failed (HTTP ${createRes.status})`);
      }
      const projectId = (createJson as any)?.data?.id as string | undefined;
      if (!projectId) throw new Error('Project create succeeded but did not return an id');

      // 2) If requested, trigger repo provisioning (Make.com).
      if (createProjectCreateRepo) {
        const repoRes = await fetch(`/api/projects/${projectId}/request-github-repo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requirements_document_id: createProjectDocId ? createProjectDocId : undefined,
          }),
        });
        const repoJson = await repoRes.json().catch(() => ({} as any));
        if (!repoRes.ok || (repoJson as any)?.success !== true) {
          throw new Error((repoJson as any)?.message ?? `Repo provisioning request failed (HTTP ${repoRes.status})`);
        }
      }

      resetCreateProjectState();
      navigate(`/projects/${projectId}`);
    } catch (err: unknown) {
      setCreateProjectError(err instanceof Error ? err.message : 'Failed to create project.');
    } finally {
      setCreateProjectSaving(false);
    }
  };

  // Populate form when entering edit mode
  const startEditing = () => {
    if (!person) return;
    setForm({
      full_name: person.full_name ?? '',
      email: person.email ?? '',
      phone: person.phone ?? '',
      role_or_title: person.role_or_title ?? '',
      status: person.status ?? '',
      newsletter_consent: person.newsletter_consent ?? false,
      website_or_profile_links: person.website_or_profile_links ?? '',
      general_notes: person.general_notes ?? '',
    });
    setSaveError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!id || !person) return;
    if (!form.full_name.trim()) {
      setSaveError('Name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/people/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          role_or_title: form.role_or_title.trim() || null,
          status: form.status,
          newsletter_consent: form.newsletter_consent,
          website_or_profile_links: form.website_or_profile_links.trim() || null,
          general_notes: form.general_notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      const json = await res.json() as { success: boolean; data: PersonDetail };
      if (!json.success) throw new Error('Save failed');
      setPerson({ ...json.data, linked_projects: person.linked_projects, documents: person.documents });
      setEditing(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save changes.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="detail-loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !person) {
    return (
      <Card>
        <ErrorState
          message={error ?? 'Person not found.'}
          onRetry={error !== 'Person not found.' ? fetchPerson : undefined}
        />
      </Card>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="people-detail-page">
      {/* Back navigation */}
      <button className="back-link" onClick={() => navigate('/people')} type="button">
        ← People
      </button>

      {/* Page header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <h1 className="detail-name">{person.full_name}</h1>
          {person.role_or_title && (
            <p className="detail-role">{person.role_or_title}</p>
          )}
        </div>
        <div className="detail-header-right">
          {!editing ? (
            <div className="people-header-actions">
              <div className="people-header-status-row">
                <select
                  className="input people-header-status-select"
                  value={statusDraft}
                  disabled={statusSaving}
                  aria-label="Relationship status"
                  onChange={(e) => {
                    const next = e.target.value;
                    setStatusDraft(next);
                    setStatusError(null);
                    void handleSaveStatus(next);
                  }}
                >
                  {PERSON_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {statusSaving && <span className="people-header-saving">Saving…</span>}
              </div>

              {statusError && <p className="people-header-error">{statusError}</p>}
              {deleteError && <p className="people-header-error">{deleteError}</p>}
              {person.merged_into_person_id && (
                <p className="people-header-merged-note">
                  Merged into: {person.merged_into_person_id}
                </p>
              )}

              <div className="people-header-buttons">
                <Button variant="secondary" size="sm" onClick={startEditing}>
                  Edit
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openUploadDocDialog}
                  disabled={uploadingDoc}
                >
                  Upload document
                </Button>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={openCreateProjectDialog}
                  disabled={createProjectSaving}
                >
                  Create project
                </Button>
                {!person.merged_into_person_id && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => (mergeOpen ? resetMergeState() : openMergePanel())}
                    disabled={mergeSaving}
                  >
                    {mergeOpen ? 'Close merge' : 'Merge'}
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeletePerson}
                  disabled={deleteSaving}
                >
                  {deleteSaving ? 'Deleting…' : 'Delete'}
                </Button>
              </div>
            </div>
          ) : (
            <Badge variant={statusVariant(person.status)}>{person.status}</Badge>
          )}
        </div>
      </div>

      {/* Upload document dialog */}
      {!editing && uploadDocOpen && (
        <Card>
          <h2 className="section-title">Upload document</h2>
          <div className="edit-form">
            <div className="input-wrapper">
              <label className="input-label">Document type</label>
              <select
                className="input"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value)}
                disabled={uploadingDoc}
              >
                <option value="Meeting notes">Meeting notes</option>
                <option value="Signed document">Signed document</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="input-wrapper">
              <label className="input-label">File</label>
              <input
                type="file"
                className="input"
                onChange={(e) => {
                  setUploadDocFile(e.target.files?.[0] || null);
                  setUploadDocError(null);
                }}
                disabled={uploadingDoc}
              />
            </div>

            {uploadDocError && <p className="edit-save-error">{uploadDocError}</p>}

            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={handleUploadDocument}
                disabled={uploadingDoc || !uploadDocFile}
              >
                {uploadingDoc ? 'Uploading…' : 'Upload'}
              </Button>
              <Button variant="secondary" onClick={resetUploadDocState} disabled={uploadingDoc}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create Project dialog (quick-create) */}
      {!editing && createProjectOpen && (
        <Card>
          <h2 className="section-title">Create project</h2>
          <div className="edit-form">
            <Input
              label="Project name *"
              value={createProjectName}
              onChange={(e) => setCreateProjectName(e.target.value)}
              placeholder="e.g. Website build"
            />

            <div className="input-wrapper">
              <label className="input-label">Requirements document (optional)</label>
              <select
                className="input"
                value={createProjectDocId}
                onChange={(e) => setCreateProjectDocId(e.target.value)}
                disabled={createProjectSaving}
              >
                <option value="">— None —</option>
                {(() => {
                  const docs = (person.documents ?? []) as PersonDocument[];
                  const meeting = docs.filter((d) => d.document_type === 'Meeting notes');
                  const other = docs.filter((d) => d.document_type !== 'Meeting notes');
                  const ordered = [...meeting, ...other];
                  return ordered.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.document_name} ({d.document_type})
                    </option>
                  ));
                })()}
              </select>
              {(person.documents ?? []).length === 0 && (
                <p className="detail-field-empty" style={{ marginTop: 'var(--spacing-xs)' }}>
                  No documents uploaded for this person yet.
                </p>
              )}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input
                type="checkbox"
                checked={createProjectCreateRepo}
                onChange={(e) => setCreateProjectCreateRepo(e.target.checked)}
                disabled={createProjectSaving}
              />
              <span>Create GitHub repo</span>
            </label>

            {createProjectError && <p className="edit-save-error">{createProjectError}</p>}

            <div className="edit-actions">
              <Button variant="primary" onClick={handleCreateProject} disabled={createProjectSaving}>
                {createProjectSaving ? 'Creating…' : 'Create'}
              </Button>
              <Button variant="secondary" onClick={resetCreateProjectState} disabled={createProjectSaving}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── View mode ─────────────────────────────────────────────────────────── */}
      {!editing ? (
        <>
          {/* Contact details */}
          <Card>
            <h2 className="section-title">Contact details</h2>
            <div className="detail-fields">
              {person.email ? (
                <div className="detail-field">
                  <span className="detail-field-label">Email</span>
                  <a href={`mailto:${person.email}`} className="detail-field-link">
                    {person.email}
                  </a>
                </div>
              ) : (
                <div className="detail-field">
                  <span className="detail-field-label">Email</span>
                  <span className="detail-field-empty">—</span>
                </div>
              )}
              {person.phone ? (
                <div className="detail-field">
                  <span className="detail-field-label">Phone</span>
                  <a href={`tel:${person.phone}`} className="detail-field-link">
                    {person.phone}
                  </a>
                </div>
              ) : (
                <div className="detail-field">
                  <span className="detail-field-label">Phone</span>
                  <span className="detail-field-empty">—</span>
                </div>
              )}
              {person.website_or_profile_links && (
                <div className="detail-field">
                  <span className="detail-field-label">Links</span>
                  <a
                    href={person.website_or_profile_links}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-field-link"
                  >
                    {person.website_or_profile_links}
                  </a>
                </div>
              )}
              {person.source && (
                <div className="detail-field">
                  <span className="detail-field-label">Source</span>
                  <span className="detail-field-value">{person.source}</span>
                </div>
              )}
              <div className="detail-field">
                <span className="detail-field-label">Newsletter consent</span>
                <span className="detail-field-value">
                  {person.newsletter_consent ? 'Consented' : 'Not consented'}
                </span>
              </div>
            </div>
          </Card>

          {/* General notes */}
          <Card>
            <h2 className="section-title">Notes</h2>
            {person.general_notes ? (
              <p className="detail-notes">{person.general_notes}</p>
            ) : (
              <p className="detail-field-empty">No notes yet.</p>
            )}
          </Card>

          {/* Documents */}
          <Card>
            <h2 className="section-title">Documents</h2>
            {(person.documents ?? []).length === 0 ? (
              <p className="detail-field-empty">No documents uploaded.</p>
            ) : (
              <div className="detail-projects-list">
                {(person.documents ?? []).map((d) => (
                  <div
                    key={d.id}
                    className="detail-project-row"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <a
                      href={`/api/documents/${d.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        cursor: 'pointer',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                      }}
                    >
                      <span className="detail-project-name">{d.document_name}</span>
                      <Badge variant="default">{d.document_type}</Badge>
                    </a>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteDocument(d.id, d.document_name)}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}

          </Card>

          {/* Client portal access */}
          <Card>
            <h2 className="section-title">Client portal access</h2>

            {!person.client_username && !grantFormOpen && (
              <>
                <p className="detail-field-empty">No client portal login granted.</p>
                <div className="edit-actions">
                  <Button variant="primary" size="sm" onClick={openGrantForm}>
                    Grant client access
                  </Button>
                </div>
              </>
            )}

            {grantFormOpen && (
              <div className="edit-form">
                <Input
                  label="Username *"
                  value={grantUsername}
                  onChange={(e) => setGrantUsername(e.target.value)}
                  placeholder="e.g. matt.ewgroup"
                  disabled={clientAccessSaving}
                />
                {!person.email && (
                  <p className="edit-save-error">
                    This person needs an email address on file before granting client access.
                  </p>
                )}
                {clientAccessError && <p className="edit-save-error">{clientAccessError}</p>}
                <div className="edit-actions">
                  <Button
                    variant="primary"
                    onClick={submitGrantClientAccess}
                    disabled={clientAccessSaving || !person.email}
                  >
                    {clientAccessSaving ? 'Granting…' : 'Grant access'}
                  </Button>
                  <Button variant="secondary" onClick={() => setGrantFormOpen(false)} disabled={clientAccessSaving}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {person.client_username && !grantFormOpen && (
              <div className="detail-fields">
                <div className="detail-field">
                  <span className="detail-field-label">Username</span>
                  <span className="detail-field-value">{person.client_username}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-field-label">Status</span>
                  <Badge variant={person.client_access_enabled ? 'success' : 'default'}>
                    {person.client_access_enabled ? 'Enabled' : 'Revoked'}
                  </Badge>
                </div>

                {clientAccessError && <p className="edit-save-error">{clientAccessError}</p>}

                <div className="edit-actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={submitGrantClientAccess}
                    disabled={clientAccessSaving}
                  >
                    {clientAccessSaving
                      ? 'Sending…'
                      : person.client_access_enabled
                        ? 'Resend set-password link'
                        : 'Re-enable access'}
                  </Button>
                  {!!person.client_access_enabled && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handlePreviewClientSession}
                      disabled={previewingClient}
                    >
                      {previewingClient ? 'Opening…' : 'View as client'}
                    </Button>
                  )}
                  {!!person.client_access_enabled && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleRevokeClientAccess}
                      disabled={clientAccessSaving}
                    >
                      Revoke access
                    </Button>
                  )}
                </div>
              </div>
            )}

            {clientAccessResult && (
              <div className="detail-fields" style={{ marginTop: 'var(--spacing-md)' }}>
                {clientAccessResult.email_sent ? (
                  <p className="detail-field-value">Set-password email sent.</p>
                ) : (
                  <>
                    <p className="edit-save-error">
                      The email failed to send. Share this set-password link with them directly:
                    </p>
                    <div className="detail-field">
                      <a
                        href={clientAccessResult.set_password_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-field-link"
                        style={{ wordBreak: 'break-all' }}
                      >
                        {clientAccessResult.set_password_url}
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>

          {/* Signing requests */}
          <SigningRequests
            personId={person.id}
            requests={person.signing_requests ?? []}
            onChanged={fetchPerson}
          />

          {/* Merge panel (opened from header quick actions) */}
          {!person.merged_into_person_id && mergeOpen && (
            <Card>
              <h2 className="section-title">Merge</h2>
              <div className="merge-panel">
                <p className="merge-help">
                  Primary person: <strong>{person.full_name}</strong>
                  <br />
                  Search for the duplicate person to merge into this record.
                </p>

                <Input
                  label="Search duplicate"
                  value={mergeQuery}
                  onChange={(e) => {
                    setMergeQuery(e.target.value);
                    setMergeSelected(null);
                    setMergeError(null);
                  }}
                  placeholder="Type a name, email, or company…"
                />

                {mergeSearching && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-sm)' }}>
                    <LoadingSpinner size="sm" />
                  </div>
                )}

                {!mergeSearching && mergeQuery.trim().length >= 2 && !mergeSelected && mergeResults.length === 0 && (
                  <p className="detail-field-empty">No people found.</p>
                )}

                {!mergeSelected && mergeResults.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                    {mergeResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="detail-person-row"
                        onClick={() => {
                          setMergeSelected(p);
                          setMergeQuery(p.full_name);
                          setMergeResults([]);
                          setMergeError(null);
                        }}
                        style={{ textAlign: 'left' }}
                      >
                        <div className="detail-person-info">
                          <span className="detail-person-name">{p.full_name}</span>
                          <span className="detail-person-role">
                            {p.email ?? '—'}{p.company_name ? ` • ${p.company_name}` : ''}
                          </span>
                        </div>
                        <Badge variant={statusVariant(p.status)}>{p.status}</Badge>
                      </button>
                    ))}
                  </div>
                )}

                {mergeSelected && (
                  <div className="merge-preview">
                    <p className="merge-preview-title">Duplicate selected</p>
                    <p className="merge-preview-body">
                      <strong>{mergeSelected.full_name}</strong>
                      {mergeSelected.email ? ` — ${mergeSelected.email}` : ''}
                      <br />
                      ID: {mergeSelected.id}
                      {mergeSelected.company_name ? `\nCompany: ${mergeSelected.company_name}` : ''}
                    </p>
                  </div>
                )}

                {mergeError && <p className="merge-error">{mergeError}</p>}

                <div className="merge-actions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={confirmMerge}
                    disabled={mergeSaving || !mergeSelected}
                  >
                    {mergeSaving ? 'Merging…' : 'Merge into primary'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={resetMergeState}
                    disabled={mergeSaving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Company */}
          <Card>
            <h2 className="section-title">Company</h2>
            {person.company_id && person.company_name ? (
              <Link to={`/companies/${person.company_id}`} className="detail-company-link">
                {person.company_name}
              </Link>
            ) : (
              <p className="detail-field-empty">No company linked.</p>
            )}
          </Card>

          {/* Linked projects */}
          <Card>
            <h2 className="section-title">Projects</h2>
            {person.linked_projects.length === 0 ? (
              <p className="detail-field-empty">No projects linked.</p>
            ) : (
              <div className="detail-projects-list">
                {person.linked_projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="detail-project-row"
                  >
                    <span className="detail-project-name">{project.project_name}</span>
                    <Badge variant={projectStatusVariant(project.status)}>
                      {project.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          {/* Metadata */}
          <div className="detail-meta">
            <span>Created {formatDate(person.created_at)}</span>
            <span>Updated {formatDate(person.updated_at)}</span>
          </div>
        </>
      ) : (
        /* ── Edit mode ──────────────────────────────────────────────────────── */
        <Card>
          <h2 className="section-title">Edit person</h2>
          <div className="edit-form">
            <Input
              label="Full name *"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Full name"
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@example.com"
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+44 7700 000000"
            />
            <Input
              label="Role / title"
              value={form.role_or_title}
              onChange={(e) => setForm({ ...form, role_or_title: e.target.value })}
              placeholder="e.g. Founder"
            />
            <div className="input-wrapper">
              <label className="input-label">Status</label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {PERSON_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="input-wrapper edit-checkbox-row">
              <label className="edit-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.newsletter_consent}
                  onChange={(e) => setForm({ ...form, newsletter_consent: e.target.checked })}
                  className="edit-checkbox"
                />
                <span>Consent to newsletter / related communications</span>
              </label>
            </div>
            <Input
              label="Website / profile links"
              value={form.website_or_profile_links}
              onChange={(e) => setForm({ ...form, website_or_profile_links: e.target.value })}
              placeholder="https://linkedin.com/in/…"
            />
            <div className="input-wrapper">
              <label className="input-label">Notes</label>
              <textarea
                className="input edit-notes-textarea"
                value={form.general_notes}
                onChange={(e) => setForm({ ...form, general_notes: e.target.value })}
                placeholder="General notes about this person…"
                rows={5}
              />
            </div>

            {saveError && (
              <p className="edit-save-error">{saveError}</p>
            )}

            <div className="edit-actions">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                variant="secondary"
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PeopleDetail;
